/**
 * The placement rules, as pure functions over block definitions. §4.2 of
 * develop/DungeonBlocks.md calls these three the complete connection contract:
 *
 * 1. Blocks may not overlap.
 * 2. A placed block must mate at least one port with an already-placed port. The first
 *    block on an empty board is exempt -- it has nothing to mate with.
 * 3. Any port left unmated seals: it renders as wall and blocks movement.
 *
 * **Connections are derived, never stored.** Two ports that meet are connected, and that
 * is computable from footprints, rotations and mirroring -- so `connections` recomputes it
 * on demand rather than anything keeping a list that can disagree with the geometry. The
 * same call `pack.animations` makes for the character pack (§10).
 *
 * The catalogue is passed in rather than read from a module-level registry: `shared` holds
 * types and arithmetic and no state, and a level refers to blocks by id without containing
 * them, so the lookup belongs to whoever is holding the board.
 */
import {
  oppositeSide,
  orientCell,
  orientPort,
  portCells,
  sideStep,
  translateCell,
  translatePort,
  type BlockDefinition,
  type BlockId,
  type BlockRotation,
  type Cell,
  type Footprint,
  type PlacedBlock,
  type Port,
  type Side,
} from './blocks.ts';

/** Block definitions by id, the shape the editor's catalogue and a block manifest take. */
export type BlockCatalogue = Record<BlockId, BlockDefinition>;

/**
 * One boundary between two cells, named once so the two blocks either side of it agree.
 * `axis: 'z'` is the edge between (x, z) and (x, z + 1) -- a horizontal line in plan view;
 * `axis: 'x'` is the edge between (x, z) and (x + 1, z).
 */
export interface Edge {
  x: number;
  z: number;
  axis: 'x' | 'z';
}

/** The edge a cell's named side opens through. */
export function portEdge(cell: Cell, side: Side): Edge {
  switch (side) {
    case 'north':
      return { x: cell.x, z: cell.z - 1, axis: 'z' };
    case 'south':
      return { x: cell.x, z: cell.z, axis: 'z' };
    case 'west':
      return { x: cell.x - 1, z: cell.z, axis: 'x' };
    case 'east':
      return { x: cell.x, z: cell.z, axis: 'x' };
  }
}

/** Every edge a port opens through: one, or the adjacent pair of a double port. */
export function portEdges(port: Port): Edge[] {
  return portCells(port).map((cell) => portEdge(cell, port.side));
}

export function edgeKey(edge: Edge): string {
  return `${edge.axis}:${edge.x}:${edge.z}`;
}

/** The two cells an edge separates. */
export function edgeCells(edge: Edge): [Cell, Cell] {
  return edge.axis === 'z'
    ? [
        { x: edge.x, z: edge.z },
        { x: edge.x, z: edge.z + 1 },
      ]
    : [
        { x: edge.x, z: edge.z },
        { x: edge.x + 1, z: edge.z },
      ];
}

/** Every cell a footprint covers, origin-relative, in reading order. */
export function footprintCells(footprint: Footprint): Cell[] {
  const cells: Cell[] = [];
  for (let z = 0; z < footprint.depth; z += 1) {
    for (let x = 0; x < footprint.width; x += 1) {
      cells.push({ x, z });
    }
  }
  return cells;
}

/** A block definition turned, with its footprint origin back at (0, 0). */
export interface OrientedBlock {
  footprint: Footprint;
  cells: Cell[];
  ports: Port[];
}

/**
 * Turn a definition and re-anchor it. Rotation and mirroring put cells at negative
 * coordinates, so both the cells and the ports are translated by the same offset -- which
 * is why they are oriented together rather than one at a time.
 */
export function orientBlock(
  definition: BlockDefinition,
  rotation: BlockRotation,
  mirrored = false,
): OrientedBlock {
  const turned = footprintCells(definition.footprint).map((cell) =>
    orientCell(cell, rotation, mirrored),
  );
  const minX = Math.min(...turned.map((cell) => cell.x));
  const minZ = Math.min(...turned.map((cell) => cell.z));
  const offset: Cell = { x: -minX, z: -minZ };
  const swapped = rotation === 90 || rotation === 270;
  return {
    footprint: swapped
      ? { width: definition.footprint.depth, depth: definition.footprint.width }
      : { ...definition.footprint },
    cells: turned.map((cell) => translateCell(cell, offset)),
    ports: definition.ports.map((port) =>
      translatePort(orientPort(port, rotation, mirrored), offset),
    ),
  };
}

/** A placed block resolved against the catalogue: where its cells and ports actually are. */
export interface ResolvedBlock {
  placed: PlacedBlock;
  definition: BlockDefinition;
  footprint: Footprint;
  cells: Cell[];
  ports: Port[];
}

/** Resolve one placement into board coordinates, or `undefined` when its block is unknown. */
export function resolvePlacement(
  placed: PlacedBlock,
  catalogue: BlockCatalogue,
): ResolvedBlock | undefined {
  const definition = catalogue[placed.block];
  if (!definition) return undefined;
  const oriented = orientBlock(definition, placed.rotation, placed.mirrored ?? false);
  return {
    placed,
    definition,
    footprint: oriented.footprint,
    cells: oriented.cells.map((cell) => translateCell(cell, placed.at)),
    ports: oriented.ports.map((port) => translatePort(port, placed.at)),
  };
}

/** One port of one placed block. `port` is in board coordinates. */
export interface PortRef {
  instanceId: string;
  block: BlockId;
  portIndex: number;
  port: Port;
}

/** Two ports that meet. Derived; never stored. */
export interface Connection {
  a: PortRef;
  b: PortRef;
  edges: Edge[];
}

/** A port with nothing across it. Rule 3: it renders as wall and blocks movement. */
export type SealedPort = PortRef;

function portRefs(blocks: ResolvedBlock[]): PortRef[] {
  return blocks.flatMap((block) =>
    block.ports.map((port, portIndex) => ({
      instanceId: block.placed.instanceId,
      block: block.placed.block,
      portIndex,
      port,
    })),
  );
}

/** A port's identity on the board: the set of edges it opens through. */
function edgeSignature(port: Port): string {
  return portEdges(port).map(edgeKey).sort().join('|');
}

/**
 * Which ports of these blocks meet. Two ports mate when they open through the same edges,
 * face opposite ways and are the same width -- so a single port offered a double one does
 * not mate, and both seal instead. That is the one-port-size contract (§4.2) doing its job
 * rather than a special case.
 */
export function connections(blocks: ResolvedBlock[]): Connection[] {
  const bySignature = new Map<string, PortRef[]>();
  for (const ref of portRefs(blocks)) {
    const signature = edgeSignature(ref.port);
    const refs = bySignature.get(signature);
    if (refs) refs.push(ref);
    else bySignature.set(signature, [ref]);
  }

  const found: Connection[] = [];
  for (const refs of bySignature.values()) {
    const taken = new Set<number>();
    for (let i = 0; i < refs.length; i += 1) {
      const a = refs[i];
      if (!a || taken.has(i)) continue;
      for (let j = i + 1; j < refs.length; j += 1) {
        const b = refs[j];
        if (!b || taken.has(j)) continue;
        if (a.instanceId === b.instanceId) continue;
        if (a.port.width !== b.port.width) continue;
        if (oppositeSide(a.port.side) !== b.port.side) continue;
        taken.add(i);
        taken.add(j);
        found.push({ a, b, edges: portEdges(a.port) });
        break;
      }
    }
  }
  return found;
}

/** Every port on the board with nothing mated across it. */
export function sealedPorts(blocks: ResolvedBlock[]): SealedPort[] {
  const mated = new Set<string>();
  for (const connection of connections(blocks)) {
    mated.add(`${connection.a.instanceId}#${connection.a.portIndex}`);
    mated.add(`${connection.b.instanceId}#${connection.b.portIndex}`);
  }
  return portRefs(blocks).filter((ref) => !mated.has(`${ref.instanceId}#${ref.portIndex}`));
}

/** Which of the three rules a refusal cites, plus the one precondition that is not a rule. */
export const PLACEMENT_RULES = ['overlap', 'mate', 'unknown-block'] as const;
export type PlacementRule = (typeof PLACEMENT_RULES)[number];

export interface PlacementAccepted {
  accepted: true;
  /** The candidate, resolved into board coordinates. */
  block: ResolvedBlock;
  /** Every connection on the board once the candidate is on it. */
  mated: Connection[];
  /** Every port left unmated on the board, candidate included -- rule 3's walls. */
  sealed: SealedPort[];
}

export interface PlacementRefused {
  accepted: false;
  rule: PlacementRule;
  /** One sentence naming the rule, for a status line. */
  reason: string;
}

export type PlacementResult = PlacementAccepted | PlacementRefused;

/**
 * Apply the three rules to one candidate placement. Nothing is mutated and nothing is
 * stored: a caller draws the result, and asks again for the next candidate.
 */
export function placeBlock(
  placed: PlacedBlock[],
  candidate: PlacedBlock,
  catalogue: BlockCatalogue,
): PlacementResult {
  const block = resolvePlacement(candidate, catalogue);
  if (!block) {
    return {
      accepted: false,
      rule: 'unknown-block',
      reason: `No block definition for "${candidate.block}".`,
    };
  }

  const board: ResolvedBlock[] = [];
  for (const existing of placed) {
    const resolved = resolvePlacement(existing, catalogue);
    if (!resolved) {
      return {
        accepted: false,
        rule: 'unknown-block',
        reason: `No block definition for "${existing.block}", already on the board as ${existing.instanceId}.`,
      };
    }
    board.push(resolved);
  }

  // Rule 1: blocks may not overlap.
  const occupied = new Map<string, ResolvedBlock>();
  for (const existing of board) {
    for (const cell of existing.cells) occupied.set(`${cell.x}:${cell.z}`, existing);
  }
  for (const cell of block.cells) {
    const clash = occupied.get(`${cell.x}:${cell.z}`);
    if (clash) {
      return {
        accepted: false,
        rule: 'overlap',
        reason: `Blocks may not overlap: ${candidate.block} would cover cell ${cell.x},${cell.z}, already held by ${clash.placed.block} (${clash.placed.instanceId}).`,
      };
    }
  }

  const mated = connections([...board, block]);

  // Rule 2: mate at least one port -- unless this is the first block on the board.
  if (board.length > 0) {
    const mine = mated.filter(
      (connection) =>
        connection.a.instanceId === candidate.instanceId ||
        connection.b.instanceId === candidate.instanceId,
    );
    if (mine.length === 0) {
      return {
        accepted: false,
        rule: 'mate',
        reason: `A placed block must mate at least one port: ${candidate.block} meets no open port here.`,
      };
    }
  }

  // Rule 3 is a report rather than a refusal: whatever is left unmated seals.
  return { accepted: true, block, mated, sealed: sealedPorts([...board, block]) };
}

/** Where a candidate would have to sit for one of its ports to mate the given open port. */
export function matingOrigin(
  candidate: OrientedBlock,
  candidatePortIndex: number,
  open: Port,
): Cell | undefined {
  const port = candidate.ports[candidatePortIndex];
  if (!port) return undefined;
  if (port.width !== open.width) return undefined;
  if (oppositeSide(port.side) !== open.side) return undefined;
  // The candidate's port cell has to land on the cell across the open port's edge, and
  // the port is origin-relative, so the origin is that cell less the port's own offset.
  const target = translateCell(open.cell, sideStep(open.side));
  return { x: target.x - port.cell.x, z: target.z - port.cell.z };
}

/** A whole board resolved at once: where every block is, and what that geometry implies. */
export interface ResolvedLevel {
  /** The placements that resolved, in the order the level stores them. */
  blocks: ResolvedBlock[];
  connections: Connection[];
  sealed: SealedPort[];
}

/**
 * Resolve a whole level in one call, so nothing downstream re-derives a rule.
 *
 * The client's view, the builder's header and the validator all want the same three
 * answers about the same board, and `connections` and `sealedPorts` each walk every port
 * to get them -- deriving them separately is how two readouts of one dungeon come to
 * disagree. Rule 3's walls are `sealed`.
 *
 * It takes any board's block list rather than a `DungeonLevel`, because the builder's
 * in-progress board is not a level document yet and asks exactly the same question. A
 * placement whose block the catalogue does not hold is left out of all three rather than
 * throwing; `readLevel(value, catalogue)` in `../level.ts` is the gate that refuses such
 * a document in the first place, so a level that got here has none.
 */
export function resolveLevel(
  level: { readonly blocks: readonly PlacedBlock[] },
  catalogue: BlockCatalogue,
): ResolvedLevel {
  const blocks: ResolvedBlock[] = [];
  for (const placed of level.blocks) {
    const resolved = resolvePlacement(placed, catalogue);
    if (resolved) blocks.push(resolved);
  }
  return { blocks, connections: connections(blocks), sealed: sealedPorts(blocks) };
}
